import * as lodash from "lodash";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { countryTextByIdFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";

import { extractPrimaryCategoryFromParams } from "../../routes/routeUtils";
import type { FormatterFunction, MetricKey } from "../config/tabsConfig";
import { getMetricConfig } from "../config/tabsConfig";
import type { IndustryAnalysisApiParams, GeographyTableData, GeographyTableResponse, CountryDataItem } from "../types";

/**
 * Transforms table data to include country names and ensure legacy format compatibility
 */
export function transformTableData(
    data: GeographyTableResponse | GeographyTableData | null,
): GeographyTableData | null {
    if (!data || !Array.isArray(data.Data)) {
        return null;
    }

    return {
        Data: data.Data.map((tableItem) => ({
            ...tableItem,
            CountryName: countryTextByIdFilter()(tableItem.Country),
        })),
        TotalCount: data.TotalCount,
    };
}

/**
 * Generates API parameters for industry analysis geography endpoints
 */
export function getApiParams(): IndustryAnalysisApiParams {
    const pageFilters = proNavigator.getApiParams();
    const { webSource = "Desktop", category, from, to } = pageFilters;

    const { duration, includeSubdomains = true } = proNavigator.getParams();

    const durationData = DurationService.getDurationData(duration, swSettings.getCurrentComponent().componentId);

    const isWindow = durationData.forAPI.isWindow;
    const primaryCategory = extractPrimaryCategoryFromParams(category);
    const categoryObject = categoryService.categoryQueryParamToCategoryObject(primaryCategory);

    const { forApi: keys, forDisplayApi: categoryDisplayApi } = categoryObject || {
        forApi: primaryCategory,
        forDisplayApi: primaryCategory,
    };

    return {
        webSource,
        includeSubdomains,
        category,
        from,
        to,
        isWindow,
        keys,
        categoryDisplayApi,
        timeGranularity: "Monthly",
    };
}

/**
 * Generates Excel API URL for table export
 */
export function generateTableExcelApiUrl(): string {
    const params = getApiParams();
    const apiParams = { ...params, category: params.categoryDisplayApi };
    const searchParams = new URLSearchParams();

    Object.entries(apiParams).forEach(([key, value]) => {
        if (value === undefined || value === "") {
            return;
        }
        searchParams.append(key, String(value));
    });

    return `api/geographyCategory/excel?${searchParams.toString()}`;
}

/**
 * Filters table data based on search string
 */
export function filterTableData(
    data: CountryDataItem[],
    searchString: string,
    tableSelectionProperty: string,
    countryTextByIdFilter: () => (val: any, na?: any) => string,
): CountryDataItem[] {
    if (!searchString.trim()) {
        return data;
    }

    const regex = new RegExp(lodash.escapeRegExp(searchString), "i");
    return lodash.filter(data, (dataItem: CountryDataItem) => {
        const countryName = countryTextByIdFilter()(dataItem[tableSelectionProperty]) || "";
        return regex.test(countryName);
    });
}

/**
 * Sorts table data by field and direction
 */
export function sortTableData(
    data: CountryDataItem[],
    field: string,
    sortDirection: "desc" | "asc",
): CountryDataItem[] {
    return lodash.orderBy(data, field, sortDirection);
}

export const getValueFormatter = (selectedMetric: MetricKey): FormatterFunction => {
    const config = getMetricConfig(selectedMetric);
    return config ? config.valueFormatter : (value: number) => value.toString();
};
